import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import ImageModule from 'docxtemplater-image-module-free';
import { toast } from 'react-toastify';
import NumeroALetras from '../conversiones/NumeroALetras';

export default async function GenerateContract({ services, payments, quote, location }) {

  const templatePath = location;

  const formatDateToDisplay = (value) => {
    if (!value) return '';
    try {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = String(date.getFullYear());
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return String(value);
    }
  };

  const safe = (v, fallback = '') => (v === undefined || v === null ? fallback : v);

  const day = new Date().getDate();
  const monthName = new Date().toLocaleString('default', { month: 'long' });
  const year = new Date().getFullYear();

  // Compute header date parts if not provided
  const now = new Date();
  
  const headerDay = safe(day, String(now.getDate()));
  const headerMonthName = safe(monthName, now.toLocaleString('default', { month: 'long' }));
  const headerYear = safe(year, String(now.getFullYear()));

  const servicesText = services
    .filter(Boolean)
    .map((item) => item?.servicio?.name || item?.name || '')
    .join(', ')

  const asBlock = (key, value) => value ? [{ [key]: value }] : [];

  // Runs helper: split text and flag target words to render in bold
  const HIGHLIGHT_REGEX = /\b(EL\s+VENDEDOR|EL\s+COMPRADOR)\b/gi;
  const splitToRuns = (text) => {
    const input = (text ?? '').toString();
    if (!input) return [];
    const runs = [];
    let lastIndex = 0;
    let match;
    while ((match = HIGHLIGHT_REGEX.exec(input))) {
      if (match.index > lastIndex) {
        runs.push({ text: input.slice(lastIndex, match.index), isBold: false });
      }
      runs.push({ text: match[0], isBold: true });
      lastIndex = HIGHLIGHT_REGEX.lastIndex;
    }
    if (lastIndex < input.length) {
      runs.push({ text: input.slice(lastIndex), isBold: false });
    }
    return runs;
  };

  // Línea resumen de IGV por servicios (agrupada: con y sin IGV)
  const servicesWithIGV = services
    .filter((svc) => String(svc?.igv || '').toLowerCase() === 'si')
    .map((svc) => svc?.servicio?.name || svc?.name || '')
    .filter(Boolean);

  const servicesWithoutIGV = services
    .filter((svc) => String(svc?.igv || '').toLowerCase() === 'no')
    .map((svc) => svc?.servicio?.name || svc?.name || '')
    .filter(Boolean);

  const igvServicesLine = [
    servicesWithIGV.length ? `CON IGV: ${servicesWithIGV.join(', ')}` : '',
    servicesWithoutIGV.length ? `SIN IGV: ${servicesWithoutIGV.join(', ')}` : ''
  ].filter(Boolean).join(' | ');


  // 1) Filtrar servicios únicos por contractLabelTitle (en orden de aparición)
  const seenTitles = new Set();
  const uniqueSegmentServices = [];
  services.forEach((svc) => {
    const title = svc?.servicio?.contractLabelTitle || '';
    if (!seenTitles.has(title)) {
      seenTitles.add(title);
      uniqueSegmentServices.push(svc);
    }
  });

  // 2) Construir estructura solo a partir de los servicios filtrados
  const structuredServices = uniqueSegmentServices.map((svc, segIdx) => {
    const labelItems = Array.isArray(svc?.servicio?.contractLabelItems) ? svc.servicio.contractLabelItems : [];

    const title = svc?.servicio?.contractLabelTitle || '';
    return {
      contractLabelTitle: title ? (segIdx + 1) + '. ' + title : '',
      contractLabelDescription: svc?.servicio?.contractLabelDescription || '',

      contractLabelTitleBlock: asBlock('contractLabelTitle', svc?.servicio?.contractLabelTitle || ''),
      contractLabelDescriptionBlock: asBlock('contractLabelDescription', svc?.servicio?.contractLabelDescription || ''),

      contractLabelItems: labelItems.map((it) => ({
        itemTitle: it?.itemTitle || '',
        itemDescription: it?.itemDescription || '',
        itemTitleRuns: splitToRuns(it?.itemTitle || ''),
        itemDescriptionRuns: splitToRuns(it?.itemDescription || ''),
        itemTitleBlock: asBlock('itemTitle', it?.itemTitle || ''),
        itemDescriptionBlock: asBlock('itemDescription', it?.itemDescription || ''),
        listItems: Array.isArray(it?.listItems) ? it.listItems.map((li) => ({
          itemSubtitle: li?.itemSubtitle || '',
          itemDescription: li?.itemDescription || '',
          itemSubtitleRuns: splitToRuns(li?.itemSubtitle || ''),
          itemDescriptionRuns: splitToRuns(li?.itemDescription || ''),
          itemSubtitleBlock: asBlock('itemSubtitle', li?.itemSubtitle || ''),
          itemDescriptionBlock: asBlock('itemDescription', li?.itemDescription || ''),
        })) : [],
      })),
    };
  });

  // 3) Formatear los pagos
  const paymentsFormatted = payments.map((payment) => ({
    ...payment,
    description: payment?.descripcion || '',
    price: payment?.precio.toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '',
    textPrice: NumeroALetras(payment?.precio.toFixed(2), {}, 'lower') || '',
    limitDate: payment?.fechaLimite.split('T')[0] || '',
  }));

  const success = quote?.estado === "Exito";
  const signImage = success ? (quote?.empresa?.firma?.url || '') : '';

  const dataForTemplate = {
    day: safe(headerDay, ''),
    month: safe(headerMonthName, ''),
    year: safe(headerYear, ''),

    company: safe(quote?.empresa?.razonSocial, 'Monstruo Creativo S.A.C'),
    agencyName: safe(quote?.marca?.name, 'Monstruo Creativo S.A.C'),
    companyRuc: safe(quote?.empresa?.ruc, '20609563258'),
    companyAddress: safe(quote?.empresa?.direccion, 'Av Calle. Elias Aguirre 180 Urb. Miraflores 180, Lima'),
    companyManager: safe(quote?.empresa?.representante, 'DULIO EDUARDO MOLINA RODRÍGUEZ'),
    companyManagerDNI: safe(quote?.empresa?.dniRepresentante, ''),
    brandName: safe(quote?.cliente?.razonSocial || quote?.lead?.nombreComercial, ''),
    clientName: safe(quote?.cliente?.nombreGerente, '***AGREGAR NOMBRE DEL REPRESENTANTE LEGAL DE LA EMPRESA***'),
    clientAddress: safe(quote?.cliente?.direccion || quote?.lead?.direccion, ''),
    quoteId: safe(quote?.id, ''),
    serviceList: safe(servicesText, ''),
    igvServicesLine: safe(igvServicesLine, ''),
    payments: paymentsFormatted,
    services: structuredServices,
    totalAmount: safe(quote?.precioTotal, ''),
    contractDate: safe(formatDateToDisplay(quote?.createdAt || new Date()), ''),
    signed: safe(success, false),
    signature: safe(signImage, ''),
  };

  const baseUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const buildTemplateUrl = (path) => `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;

  const fetchTemplate = async (path) => {
    const url = buildTemplateUrl(encodeURI(path));
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) return null;
    const arrayBuffer = await response.arrayBuffer();
    const header = new Uint8Array(arrayBuffer, 0, 2);
    if (header[0] !== 0x50 || header[1] !== 0x4B) return null;
    return arrayBuffer;
  };

  try {
    let arrayBuffer = await fetchTemplate(templatePath);
    if (!arrayBuffer) {
      const defaultPath = '/docs/Contratos/Contrato Monstruo Creativo.docx';
      arrayBuffer = await fetchTemplate(defaultPath);
    }
    if (!arrayBuffer) {
      throw new Error('No se encontró la plantilla del contrato. Verifica que exista public/docs/Contratos/Contrato Monstruo Creativo.docx');
    }

    const imageOptions = {
      async getImage(tagValue /*, tagName */) {
        try {
          if (!tagValue) return new Uint8Array(0);
          const isDataUrl = typeof tagValue === 'string' && tagValue.startsWith('data:');
          if (isDataUrl) {
            const base64 = tagValue.split(',')[1] || '';
            const binary = atob(base64);
            const len = binary.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
            return bytes; // Uint8Array
          }
          const res = await fetch(tagValue, { cache: 'no-store', mode: 'cors' });
          if (!res.ok) throw new Error(`No se pudo obtener la imagen (${res.status})`);
          const buf = await res.arrayBuffer();
          return new Uint8Array(buf); // Uint8Array
        } catch (e) {
          return new Uint8Array(0);
        }
      },
      getSize() {
        const height = 100;
        const width = 200;
        return [width, height];
      },
    };

    const zip = new PizZip(arrayBuffer);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      nullGetter: () => '',
      modules: [new ImageModule(imageOptions)],
    });

    await doc.renderAsync(dataForTemplate);

    const out = doc.getZip().generate({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const normalizePart = (text) => String(text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9-_]+/g, '_')

    const marcaPart = normalizePart(dataForTemplate.agencyName || 'marca');
    const clientePart = normalizePart(dataForTemplate.brandName || 'cliente');
    const idPart = dataForTemplate.quoteId ? normalizePart(String(dataForTemplate.quoteId)) : '';
    const fileName = `Contrato_${marcaPart}_${clientePart}_${idPart}.docx`;

    saveAs(out, fileName);
  } catch (error) {
    // eslint-disable-next-line no-console
    toast.error('No se pudo generar el contrato');
    throw error;
  }
}